import { makeStyles } from '@material-ui/styles'
import { listTablePrefs } from 'app/constants'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import React, { useCallback, useEffect, useMemo } from 'react'
import { GridViewColumn } from 'core/elements/grid/Grid'
import ListContainer from 'core/containers/ListContainer'
import { pick, prop } from 'ramda'
import { listPorts, deletePort } from '../actions'
import { portsSelector, portsByNetworkId } from '../selectors'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import CreatePortModal from './CreatePortModal'
import EditPortModal from './EditPortModal'
import { routeManager } from 'core/utils/routes'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { RootState } from 'app/store'
import Text from 'core/elements/Text'
import DeleteDialog from 'core/components/DeleteDialog'
import { formatTimestampToLocal } from '../../vms/helpers'
import { sortIpAddress } from './helper'

type ModelDataKey = DataKeys.OpenstackPorts
type SelectorModel = ArrayElement<ReturnType<typeof portsSelector>>

const defaultParams = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const useStyles = makeStyles<Theme>((theme) => ({
  ports: {
    marginTop: '16px',
  },
  addressPairs: {
    display: 'grid',
    gap: 8,
  },
  addressPair: {
    whiteSpace: 'nowrap',
  },
}))

const AddressPairsCell = ({ value }) => {
  const classes = useStyles()
  return (
    <div className={classes.addressPairs}>
      {value?.map((pair) => (
        <div className={classes.addressPair}>
          <Text variant="body2">IP Address: {pair?.ip_address}</Text>
          <Text variant="body2">MAC Address: {pair?.mac_address}</Text>
        </div>
      ))}
    </div>
  )
}

function search(value: string | Array<string>, searchStr: string) {
  if (typeof value === 'string') {
    return value.toLowerCase().includes(searchStr.toLowerCase())
  }

  if (Array.isArray(value)) {
    const lws = searchStr.toLowerCase()
    return value.some((s) => s.toLowerCase().includes(lws))
  }
  // maybe handle nested array of objs????????????

  return false
}

type PossibleCellTypes = boolean | string | Array<{ [key: string]: string }>
function makeSearchFn<T extends PossibleCellTypes>(
  formatCellValue: (cellValue: T) => string | Array<string>,
) {
  return function searchFn(cellValue: T, searchTerm: string) {
    return search(formatCellValue(cellValue), searchTerm)
  }
}

const portColumns = ({ orderDirection }): GridViewColumn<SelectorModel>[] => {
  return [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
    },
    {
      key: 'id',
      label: 'UUID',
      display: false,
    },
    {
      key: 'fixed_ips',
      label: 'Private IPs',
      render: (value) => value.map((ip) => ip?.ip_address).join(', '),
      sortFn: (a: { [x: string]: string }[], b: { [x: string]: string }[]) =>
        sortIpAddress(
          a?.map((ip) => ip?.ip_address) || [],
          b?.map((ip) => ip?.ip_address) || [],
          orderDirection,
        ),
    },
    {
      key: 'mac_address',
      label: 'MAC Address',
    },
    {
      key: 'binding:vif_type',
      label: 'VIF Type',
    },
    {
      key: 'device_owner',
      label: 'Attachment Type',
    },
    // {
    //   key: 'device',
    //   label: 'Attached Device'
    // },
    {
      key: 'status',
      label: 'Status',
    },
    {
      key: 'port_security_enabled',
      label: 'Port Security',
      render: (value) => (value ? 'Enabled' : 'Disabled'),
    },
    {
      key: 'securityGroups',
      label: 'Security Groups',
      render: (value) => value?.map((group) => group?.name || group?.id)?.join(', '),
    },
    {
      key: 'allowed_address_pairs',
      label: 'Allowed Address Pairs',
      CellComponent: AddressPairsCell,
    },
    {
      key: 'admin_state_up',
      label: 'Admin State',
      render: (value) => (value ? 'Up' : 'Down'),
    },
    {
      key: 'dns_name',
      label: 'DNS Name',
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (time: string) => formatTimestampToLocal(time),
      display: false,
    },
  ]
}

const searchFunctions = [
  {
    property: 'mac_address',
    searchFn: makeSearchFn<string>((cellValue) => cellValue),
  },
  {
    property: 'securityGroups',
    searchFn: makeSearchFn<Array<{ name: string; id: string }>>((cellValue) =>
      cellValue?.map((group) => group?.name || group?.id)?.join(','),
    ),
  },
  {
    property: 'allowed_address_pairs',
    searchFn: makeSearchFn<Array<{ ip_address: string; mac_address: string }>>((cellValue) =>
      cellValue.map((cell) => `${cell.ip_address}__${cell.mac_address}`).join(','),
    ),
  },
  {
    property: 'port_security_enabled',
    searchFn: makeSearchFn<boolean>((cellValue) => (cellValue ? 'enabled' : 'disabled')),
  },
  {
    property: 'fixed_ips',
    // makeSearchFn<Array<{ [key: string]}>>()
    searchFn: (value, searchString) => {
      const lowerSearch = searchString?.toLowerCase()
      return value?.some(
        (ipObj) => ipObj?.ip_address && ipObj.ip_address.toLowerCase().includes(lowerSearch),
      )
    },
  },
]

const usePrefParams = createUsePrefParamsHook('Ports', listTablePrefs)

export default function PortsPage({ network, loading, allowCrud = true }) {
  const classes = useStyles()
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const isVirtualNetwork = network?.type === 'virtual'

  const { message, loading: loadingPorts, reload: reloadPorts } = useListAction(listPorts, {
    params,
  })
  const portsData = useSelector(portsByNetworkId)

  const networkPorts = useMemo(() => {
    return portsData[network.id] || []
  }, [portsData, network.id])

  const addRoute = isVmware
    ? routeManager.routes.openstack.createVmwPort
    : isVirtualNetwork
    ? routeManager.routes.openstack.createVirtualNetworkPort
    : routeManager.routes.openstack.createPhysicalNetworkPort

  // useEffect(() => {
  //   reloadPorts(true)
  // }, [])

  const columns = useMemo(() => portColumns({ orderDirection: params.orderDirection }), [
    params.orderDirection,
  ])

  const DeletePortDialog = useCallback(
    (props) => (
      <DeleteDialog
        {...props}
        deleteAction={deletePort}
        title="Delete Port(s)"
        resourceLabel="Ports"
      />
    ),
    [],
  )

  return (
    <article className={classes.ports}>
      <CreatePortModal addRoute={addRoute} isVirtualNetwork={isVirtualNetwork} />
      <ListContainer<ModelDataKey, SelectorModel>
        showBreadcrumbs={false}
        dataKey={DataKeys.OpenstackPorts}
        searchTargets={[
          'name',
          'status',
          'mac_address',
          'device_owner',
          'allowed_address_pairs',
          'port_security_enabled',
          'dns_name',
          'fixed_ips',
          'securityGroups',
        ]}
        searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading || loadingPorts}
        loadingMessage={message}
        onRefresh={() => {
          reloadPorts(true, true)
        }}
        data={networkPorts}
        columns={columns}
        DeleteDialogComponent={DeletePortDialog}
        addUrl={allowCrud ? addRoute.path({ id: network?.id }) : undefined}
        addText={allowCrud ? 'Create Port' : undefined}
        EditDialogComponent={allowCrud ? EditPortModal : undefined}
        deleteAction={deletePort}
        getParamsUpdater={getParamsUpdater}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </article>
  )
}
